import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Check,
  Copy,
  Eye,
  EyeOff,
  Loader2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import {
  StatusBadge,
  type StatusBadgeVariant,
} from "@/components/shared/StatusBadge";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  getPlatformIngresoSimplificadoDetail,
  platformStartIngresoSimplificado,
  platformSubmitPostulacion,
  platformSubmitConfirmationCode,
  platformAcceptTrabajo,
  platformMarkAuthorized,
  platformResendProduccionRoleEmail,
  platformMarkRecibido,
  platformMarkAwaitingProduccion,
  platformMarkCompleted,
  type IngresoSimplificadoStatus,
  type PlatformIngresoSimplificadoDetail,
} from "@/services/ingresoSimplificadoService";

/** Statuses where a DGI automation session is running — poll for the outcome. */
const LIVE_STATUSES: IngresoSimplificadoStatus[] = [
  "postulacion_running",
  "aceptacion_running",
];

const STATUS_LABEL: Record<IngresoSimplificadoStatus, string> = {
  pending: "Pendiente",
  portal_user_ready: "Usuario e-Factura listo",
  postulacion_running: "Postulación en curso…",
  awaiting_confirmation_code: "Esperando código de confirmación",
  postulacion_submitted: "Postulación enviada",
  awaiting_proveedor_acceptance: "Esperando aceptación del proveedor",
  aceptacion_running: "Aceptación en curso…",
  trabajo_accepted: "Trabajo aceptado",
  authorized: "Autorizado",
  recibido_sent: "Recibido enviado",
  awaiting_produccion_role: "Esperando rol de producción",
  completed: "Completada",
  vigente: "Vigente",
  failed: "Falló",
  rejected: "Rechazada",
};

const STATUS_VARIANT: Record<IngresoSimplificadoStatus, StatusBadgeVariant> = {
  pending: "warning",
  portal_user_ready: "info",
  postulacion_running: "info",
  awaiting_confirmation_code: "warning",
  postulacion_submitted: "info",
  awaiting_proveedor_acceptance: "warning",
  aceptacion_running: "info",
  trabajo_accepted: "info",
  authorized: "info",
  recibido_sent: "info",
  awaiting_produccion_role: "warning",
  completed: "success",
  vigente: "success",
  failed: "danger",
  rejected: "danger",
};

interface CredRowProps {
  label: string;
  value: string;
  mask?: boolean;
}

function CredRow({ label, value, mask }: CredRowProps): JSX.Element {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const copy = (): void => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };
  const shown = mask && !revealed ? "••••••••••" : value;
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className="truncate font-mono text-sm">{shown}</div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {mask ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? "Ocultar" : "Mostrar"}
          >
            {revealed ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </Button>
        ) : null}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={copy}
          aria-label="Copiar"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}

/**
 * Platform-admin detail + driver for one tenant's homologación simplificada.
 * Shows the tenant's DECRYPTED e-Factura credentials (so an operator can log
 * into DGI) and drives the state machine via the platform-scoped endpoints.
 * Root / root-admin only (route-guarded).
 */
export function PlatformHomologacionDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId = "" } = useParams<{ companyId: string }>();
  const [acting, setActing] = useState(false);
  const [code, setCode] = useState("");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["platform", "ingreso-simplificado", companyId] as const,
    queryFn: () => getPlatformIngresoSimplificadoDetail(companyId),
    enabled: Boolean(companyId),
    refetchInterval: (query) =>
      query.state.data && LIVE_STATUSES.includes(query.state.data.status)
        ? 5000
        : false,
  });

  const run = useCallback(
    async (
      fn: () => Promise<unknown>,
      successMsg: string,
    ): Promise<void> => {
      if (acting) return;
      setActing(true);
      try {
        await fn();
        await refetch();
        toast.success(successMsg);
      } catch {
        toast.error("No se pudo completar la acción. Reintentá.");
      } finally {
        setActing(false);
      }
    },
    [acting, refetch],
  );

  if (isLoading) return <LoadingPage />;
  if (!data) {
    return (
      <div className="fx-content space-y-4">
        <Button variant="ghost" onClick={() => navigate("/homologaciones")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver
        </Button>
        <p className="text-muted-foreground">Homologación no encontrada.</p>
      </div>
    );
  }

  const d: PlatformIngresoSimplificadoDetail = data;
  const status = d.status;

  return (
    <div className="fx-content space-y-4">
      <Button
        variant="ghost"
        className="w-fit"
        onClick={() => navigate("/homologaciones")}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Volver a homologaciones
      </Button>

      <PageHeader
        title={d.tenantName || d.companyId}
        description={`RUT ${d.tenantRut || "—"}`}
      />

      <div className="flex items-center gap-3">
        <StatusBadge variant={STATUS_VARIANT[status]}>
          {STATUS_LABEL[status]}
        </StatusBadge>
        {LIVE_STATUSES.includes(status) ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : null}
      </div>

      {d.failureReason ? (
        <Card className="border-destructive/40">
          <CardContent className="pt-4 text-sm text-destructive">
            {d.failureReason}
          </CardContent>
        </Card>
      ) : null}

      {/* e-Factura credentials */}
      <Card>
        <CardHeader>
          <CardTitle>Usuario e-Factura (DGI)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {d.dgiPortalUser ? (
            <>
              <CredRow
                label={`Documento (${d.dgiPortalUser.documentType})`}
                value={d.dgiPortalUser.documentNumber}
              />
              <CredRow
                label="Contraseña"
                value={d.dgiPortalUser.password}
                mask
              />
              <p className="text-xs text-muted-foreground">
                {d.dgiPortalUser.confirmedAt
                  ? `Confirmado por DGI el ${formatDisplayDate(
                      d.dgiPortalUser.confirmedAt,
                    )}`
                  : "Todavía sin confirmación de DGI."}
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Esta empresa todavía no generó el usuario e-Factura.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Driver */}
      <Card>
        <CardHeader>
          <CardTitle>Homologación</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <DriverActions
            status={status}
            acting={acting}
            portalUserConfirmed={Boolean(d.dgiPortalUser?.confirmedAt)}
            code={code}
            setCode={setCode}
            onStart={() =>
              void run(
                () => platformStartIngresoSimplificado(companyId),
                // NOT "creación del usuario": by this point DGI has already
                // confirmed the e-Factura user — this action only records it as
                // ready and opens the postulación. The old copy had an operator
                // asking whether the user had just been created again.
                "Usuario e-Factura registrado. Ya podés iniciar la postulación.",
              )
            }
            onPostulacion={() =>
              void run(
                () => platformSubmitPostulacion(companyId),
                "Postulación iniciada.",
              )
            }
            onSubmitCode={() =>
              void run(
                () => platformSubmitConfirmationCode(companyId, code.trim()),
                "Código enviado.",
              )
            }
            onAcceptTrabajo={() =>
              void run(
                () => platformAcceptTrabajo(companyId),
                "Aceptación iniciada.",
              )
            }
            onMarkAuthorized={() =>
              void run(
                () => platformMarkAuthorized(companyId),
                "Marcado autorizado.",
              )
            }
            onResendProduccionEmail={() =>
              void run(
                () => platformResendProduccionRoleEmail(companyId),
                "Reenviando instrucciones al cliente…",
              )
            }
            onMarkRecibido={() =>
              void run(
                () => platformMarkRecibido(companyId),
                "Marcado recibido.",
              )
            }
            onMarkAwaitingProduccion={() =>
              void run(
                () => platformMarkAwaitingProduccion(companyId),
                "Marcado esperando producción.",
              )
            }
            onMarkCompleted={() =>
              void run(
                () => platformMarkCompleted(companyId),
                "Homologación completada.",
              )
            }
          />
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Línea de tiempo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <TimelineRow label="Iniciada" value={d.createdAt} />
          <TimelineRow
            label="Postulación enviada"
            value={d.postulacionSubmittedAt}
          />
          <TimelineRow label="Trabajo aceptado" value={d.trabajoAcceptedAt} />
          <TimelineRow label="Autorizado" value={d.authorizedAt} />
          <TimelineRow label="Recibido enviado" value={d.recibidoSentAt} />
          <TimelineRow label="Completada" value={d.completedAt} />
          <TimelineRow
            label="Vigencia"
            value={d.fechaVigencia}
            raw={d.fechaVigencia ?? undefined}
          />
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        {t(
          "platformHomologaciones.detailFootnote",
          "Solicitud N.º {{n}} · actualizada {{u}}",
          {
            n: d.dgiSolicitudNumero ?? "—",
            u: formatDisplayDate(d.updatedAt),
          },
        )}
      </p>
    </div>
  );
}

function TimelineRow({
  label,
  value,
  raw,
}: {
  label: string;
  value: string | null;
  raw?: string;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between border-b py-1 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">
        {raw ?? (value ? formatDisplayDate(value) : "—")}
      </span>
    </div>
  );
}

interface DriverActionsProps {
  status: IngresoSimplificadoStatus;
  acting: boolean;
  /** Whether DGI has confirmed the tenant's e-Factura user (homologación role). */
  portalUserConfirmed: boolean;
  code: string;
  setCode: (v: string) => void;
  onStart: () => void;
  onPostulacion: () => void;
  onSubmitCode: () => void;
  onAcceptTrabajo: () => void;
  onMarkAuthorized: () => void;
  onResendProduccionEmail: () => void;
  onMarkRecibido: () => void;
  onMarkAwaitingProduccion: () => void;
  onMarkCompleted: () => void;
}

function DriverActions(props: DriverActionsProps): JSX.Element {
  const { status, acting } = props;
  const spinner = acting ? (
    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
  ) : null;

  if (LIVE_STATUSES.includes(status)) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Automatización en curso — se actualiza solo.
      </div>
    );
  }

  switch (status) {
    case "pending":
      // We do NOT create the first e-Factura user — the emisor creates it
      // themselves (with the homologación role). Until DGI confirms it there's
      // nothing for the team to do but wait; once confirmed, advance to start
      // the postulación (the start call is idempotent for an existing user).
      if (!props.portalUserConfirmed) {
        return (
          <p className="text-sm text-muted-foreground">
            Esperando a que el emisor cree el usuario e-Factura (rol Homologación
            simplificada) y a que DGI lo confirme. Cuando esté confirmado vas a
            poder iniciar la postulación desde acá.
          </p>
        );
      }
      return (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            El usuario e-Factura está confirmado por DGI. Habilitá la postulación.
          </p>
          <Button onClick={props.onStart} disabled={acting}>
            {spinner}
            Habilitar postulación
          </Button>
        </div>
      );
    case "portal_user_ready":
    case "failed":
      return (
        <Button onClick={props.onPostulacion} disabled={acting}>
          {spinner}
          Iniciar postulación
        </Button>
      );
    case "awaiting_confirmation_code":
      return (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Normalmente tomamos el código del correo de DGI automáticamente, en
            el próximo barrido del inbox. Si no avanzó, ingresalo a mano acá
            (vence a los 15 minutos).
          </p>
          <div className="flex items-center gap-2">
            <input
              className="w-40 rounded-md border px-3 py-2 font-mono text-sm"
              value={props.code}
              onChange={(e) => props.setCode(e.target.value)}
              placeholder="Código"
            />
            <Button
              onClick={props.onSubmitCode}
              disabled={acting || props.code.trim().length === 0}
            >
              {spinner}
              Enviar código
            </Button>
          </div>
        </div>
      );
    case "postulacion_submitted":
    case "awaiting_proveedor_acceptance":
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            DGI nos avisa por correo que el trabajo está pendiente de aceptación.
            Cuando llega, SALKANTAI lo acepta solo — suele tardar menos de 5
            minutos. No hace falta hacer nada.
          </p>
          <p className="text-xs text-muted-foreground">
            Usá este botón solo si el correo de DGI no llegó.
          </p>
          <Button
            variant="outline"
            onClick={props.onAcceptTrabajo}
            disabled={acting}
          >
            {spinner}
            Aceptar trabajo (SALKANTAI)
          </Button>
        </div>
      );
    case "trabajo_accepted":
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Falta que <strong>DGI autorice</strong> al emisor. Puede demorar
            hasta 5 días hábiles y no depende de nosotros. Cuando llega el correo
            de DGI se procesa solo: marcamos autorizado, le respondemos
            «Recibido» a DGI y le enviamos al cliente las instrucciones del rol de
            Producción.
          </p>
          <p className="text-xs text-muted-foreground">
            Usá este botón solo si DGI ya autorizó y el correo no llegó. Marcarlo
            a mano le responde «Recibido» a DGI, así que no lo uses antes de que
            la autorización exista.
          </p>
          <Button
            variant="outline"
            onClick={props.onMarkAuthorized}
            disabled={acting}
          >
            {spinner}
            Marcar autorizado
          </Button>
        </div>
      );
    case "authorized":
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            DGI ya autorizó. Falta responderle «Recibido» — se envía solo, en el
            mismo paso que procesó la autorización.
          </p>
          <p className="text-xs text-muted-foreground">
            Usá este botón solo si el envío falló y quedó trabado acá.
          </p>
          <Button
            variant="outline"
            onClick={props.onMarkRecibido}
            disabled={acting}
          >
            {spinner}
            Marcar recibido enviado
          </Button>
        </div>
      );
    case "recibido_sent":
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Ya le respondimos «Recibido» a DGI. El paso siguiente — avisarle al
            cliente que agregue el rol de Producción — se hace solo.
          </p>
          <p className="text-xs text-muted-foreground">
            Usá este botón solo si no avanzó por su cuenta.
          </p>
          <Button
            variant="outline"
            onClick={props.onMarkAwaitingProduccion}
            disabled={acting}
          >
            {spinner}
            Marcar esperando producción
          </Button>
        </div>
      );
    case "awaiting_produccion_role":
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Falta que el cliente agregue el rol de <strong>Producción</strong> a
            su usuario e-Factura. Lo hace él mismo en DGI — no tenemos su acceso a
            DGI Servicios en línea, así que no podemos hacerlo por él. Ya le
            enviamos por correo los pasos y sus credenciales; cuando lo agregue,
            DGI confirma por correo y la homologación se completa sola.
          </p>
          <Button onClick={props.onResendProduccionEmail} disabled={acting}>
            {spinner}
            Reenviar instrucciones al cliente
          </Button>
          <p className="text-xs text-muted-foreground">
            Si ya confirmaste con el cliente que lo agregó, podés marcarla
            completada a mano.
          </p>
          <Button
            variant="outline"
            onClick={props.onMarkCompleted}
            disabled={acting}
          >
            {spinner}
            Marcar completada
          </Button>
        </div>
      );
    case "completed":
    case "vigente":
      return (
        <p className="text-sm text-muted-foreground">
          Homologación completada. Nada más para hacer.
        </p>
      );
    case "rejected":
      return (
        <p className="text-sm text-destructive">
          Rechazada por DGI. Revisá el motivo arriba.
        </p>
      );
    default:
      return <></>;
  }
}
